/**
 * Vellatini navigation controller — desktop mega panels, sticky-header hairline,
 * mobile full-screen menu. One file for every header (shop quiet row + homepage hero).
 *
 * Desktop panels: hover opens after 80ms, leaving header+panel closes after 120ms;
 * click on the word toggles instead of navigating (touch laptops); focus opens;
 * Escape closes and returns focus; outside click closes.
 * Mobile menu: hamburger opens the overlay and locks scroll (html[scroll-lock],
 * assets/base.css); doors push a child screen; ← goes back; × / Escape close.
 */
(function () {
  var desktop = window.matchMedia('(min-width: 990px)');

  /* ── Desktop mega panels ─────────────────────────────────────────────── */
  function initMega(root) {
    var mega = root.querySelector('.vt-mega');
    if (!mega) return;
    var triggers = Array.prototype.slice.call(root.querySelectorAll('[data-vt-mega]'));
    var panels = Array.prototype.slice.call(mega.querySelectorAll('[data-vt-panel]'));
    var openTimer = null;
    var closeTimer = null;
    var current = null;

    function show(name) {
      clearTimeout(closeTimer);
      current = name;
      panels.forEach(function (p) {
        p.classList.toggle('is-active', p.getAttribute('data-vt-panel') === name);
      });
      triggers.forEach(function (t) {
        var on = t.getAttribute('data-vt-mega') === name;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-expanded', on ? 'true' : 'false');
      });
      mega.setAttribute('data-vt-panel-open', name);
      root.classList.add('is-mega-open');
    }

    function hide() {
      clearTimeout(openTimer);
      clearTimeout(closeTimer);
      current = null;
      mega.removeAttribute('data-vt-panel-open');
      root.classList.remove('is-mega-open');
      panels.forEach(function (p) { p.classList.remove('is-active'); });
      triggers.forEach(function (t) {
        t.classList.remove('is-active');
        t.setAttribute('aria-expanded', 'false');
      });
    }

    function scheduleClose() {
      clearTimeout(openTimer);
      clearTimeout(closeTimer);
      closeTimer = setTimeout(hide, 120);
    }

    triggers.forEach(function (t) {
      var name = t.getAttribute('data-vt-mega');
      t.setAttribute('aria-haspopup', 'true');
      t.setAttribute('aria-expanded', 'false');
      t.addEventListener('mouseenter', function () {
        if (!desktop.matches) return;
        clearTimeout(closeTimer);
        clearTimeout(openTimer);
        openTimer = setTimeout(function () { show(name); }, 80);
      });
      t.addEventListener('click', function (e) {
        if (!desktop.matches) return;
        e.preventDefault();
        if (current === name) hide(); else show(name);
      });
      t.addEventListener('focus', function () {
        if (desktop.matches) show(name);
      });
    });

    root.addEventListener('mouseleave', function () {
      if (desktop.matches) scheduleClose();
    });
    mega.addEventListener('mouseenter', function () { clearTimeout(closeTimer); });
    root.addEventListener('focusout', function (e) {
      if (!root.contains(e.relatedTarget)) hide();
    });
    document.addEventListener('click', function (e) {
      if (current && !root.contains(e.target)) hide();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' || !current) return;
      var t = triggers.filter(function (x) { return x.getAttribute('data-vt-mega') === current; })[0];
      hide();
      if (t) t.focus();
    });
    desktop.addEventListener('change', function (m) { if (!m.matches) hide(); });
  }

  /* ── Sticky hairline on the shop header ─────────────────────────────── */
  function initStuck(header) {
    var group = header.closest('#header-group') || header.parentNode;
    if (!group || !('IntersectionObserver' in window)) return;
    // In-flow 1px sentinel pulled back with a negative margin so it costs no layout;
    // an absolutely positioned one resolved against the wrong containing block and
    // reported "stuck" at the top of the page.
    var sentinel = document.createElement('div');
    sentinel.setAttribute('aria-hidden', 'true');
    sentinel.style.cssText = 'height:1px;margin-bottom:-1px;pointer-events:none;visibility:hidden;';
    group.parentNode.insertBefore(sentinel, group);
    // ≥990 the page scrolls inside .page-wrapper (base.css), below that the document does.
    var wrapper = document.querySelector('.page-wrapper');
    var scrollRoot = desktop.matches && wrapper && wrapper.contains(group) ? wrapper : null;
    new IntersectionObserver(function (entries) {
      header.classList.toggle('is-stuck', !entries[0].isIntersecting);
    }, { root: scrollRoot, threshold: 0 }).observe(sentinel);
  }

  /* ── Mobile full-screen menu ─────────────────────────────────────────── */
  function initMobileMenu() {
    var menu = document.querySelector('.vt-mm');
    if (!menu) return;
    var screens = Array.prototype.slice.call(menu.querySelectorAll('.vt-mm__screen--child'));
    var lastOpener = null;

    function reset() {
      screens.forEach(function (s) { s.classList.remove('is-active'); });
    }
    function open(opener) {
      lastOpener = opener || null;
      reset();
      menu.hidden = false;
      document.documentElement.setAttribute('scroll-lock', '');
      var closeBtn = menu.querySelector('[data-vt-menu-close]');
      if (closeBtn) closeBtn.focus({ preventScroll: true });
    }
    function close() {
      menu.hidden = true;
      document.documentElement.removeAttribute('scroll-lock');
      reset();
      if (lastOpener) lastOpener.focus();
    }

    document.addEventListener('click', function (e) {
      var opener = e.target.closest('[data-vt-menu-open]');
      if (opener) { e.preventDefault(); open(opener); return; }
      if (!menu.contains(e.target)) return;
      // "The story" sits inside the material row's own link (no nested <a>): route it by hand.
      var story = e.target.closest('[data-vt-menu-story]');
      if (story) { e.preventDefault(); window.location.href = story.getAttribute('data-href'); return; }
      var go = e.target.closest('[data-vt-menu-go]');
      if (go) {
        e.preventDefault();
        var target = menu.querySelector('[data-vt-menu-screen="' + go.getAttribute('data-vt-menu-go') + '"]');
        if (target) {
          target.classList.add('is-active');
          target.scrollTop = 0;
          // focus() without preventScroll scrolled the overflow-hidden overlay
          // sideways to reveal the off-screen child (screens sat at x = -225).
          var back = target.querySelector('[data-vt-menu-back]');
          if (back) back.focus({ preventScroll: true });
          menu.scrollLeft = 0;
        }
        return;
      }
      if (e.target.closest('[data-vt-menu-back]')) { e.preventDefault(); reset(); return; }
      if (e.target.closest('[data-vt-menu-close]')) { e.preventDefault(); close(); }
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !menu.hidden) close();
    });
    desktop.addEventListener('change', function (m) { if (m.matches && !menu.hidden) close(); });
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('.vt-nav-root'), initMega);
    var header = document.getElementById('header-component');
    if (header) initStuck(header);
    initMobileMenu();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
